import { useAuth, AuthProvider } from "./AuthContext";
import LoginPage from "./LoginPage";
import SuperAdmin from "./SuperAdmin";
import Sidebar from "./Sidebar";
import Dashboard from "./Dashboard";
import SubscriberManagement from "./SubscriberManagement";
import Deliveries from "./Deliveries";
import Leads from "./Leads";
import ProviderSettings from "./ProviderSettings";
import AddSubscriberQuick from "./AddSubscriberQuick";
import LandingPage from "./LandingPage";
import Billing from "./Billing";
import SubscriberPortal from "./SubscriberPortal";
import { useState, useEffect } from "react";

const NAV = [
  { key: "dashboard",   icon: "▦", label: "Home"        },
  { key: "subscribers", icon: "◎", label: "Subscribers" },
  { key: "deliveries",  icon: "◉", label: "Deliveries"  },
  { key: "leads",       icon: "◈", label: "Leads"       },
  { key: "settings",    icon: "🛒", label: "Marketplace" },
];

function AppInner() {
  const { user, isSuperAdmin, logout, loadingUsers } = useAuth();
  const [page, setPage] = useState("dashboard");

  const isAddSubscriberRoute =
    window.location.pathname === "/add-subscriber" ||
    window.location.search.includes("name=") ||
    window.location.search.includes("phone=");

  useEffect(() => {
    if (user && !isSuperAdmin) {
      const saved = sessionStorage.getItem("mealbox_redirect");
      if (saved) { sessionStorage.removeItem("mealbox_redirect"); window.location.href = saved; }
    }
  }, [user, isSuperAdmin]);

  // Safety net — uses module-level constant, guaranteed same value
  if (PORTAL_TOKEN) {
    return <SubscriberPortal token={PORTAL_TOKEN} />;
  }

  if (loadingUsers && !user) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#F7F6F2", fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 20, fontWeight: 600, color: "#1C1917", marginBottom: 8 }}>MealBox</div>
          <div style={{ fontSize: 13, color: "#78716C" }}>Loading…</div>
        </div>
      </div>
    );
  }

  // Show landing page only on the public domain, not the app subdomain
  const isAppDomain = window.location.hostname.includes("app.") ||
                      window.location.hostname.includes("vercel.app") ||
                      window.location.hostname === "localhost";
  if (!user && !isAppDomain) return <LandingPage />;
  if (!user) return <LoginPage />;
  if (isSuperAdmin) return <SuperAdmin />;
  if (isAddSubscriberRoute) return <AddSubscriberQuick />;

  // When a lead is converted → switch to Subscribers page with prefill
  const handleLeadConvert = (lead) => {
    setPage("subscribers");
  };

  const renderPage = () => {
    switch (page) {
      case "dashboard":   return <Dashboard onNavigate={setPage} userId={user.id} />;
      case "subscribers": return <SubscriberManagement onNavigate={setPage} userId={user.id} />;
      case "deliveries":  return <Deliveries onNavigate={setPage} userId={user.id} />;
      case "leads":       return <Leads userId={user.id} onConvert={handleLeadConvert} />;
      case "settings":    return <ProviderSettings />;
      case "billing":     return <Billing userId={user.id} />;
      default:
        return (
          <div style={{ padding: "3rem 2rem", fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
            <h2 style={{ fontSize: 20, fontWeight: 600, color: "#1C1917" }}>Coming soon</h2>
            <p style={{ color: "#78716C", marginTop: 8, fontSize: 14 }}>This module is under development.</p>
          </div>
        );
    }
  };

  return (
    <div className="app-shell">
      <Sidebar activePage={page} onNavigate={setPage} user={user} onLogout={logout} />
      <main className="app-main">{renderPage()}</main>
      <nav className="bottom-nav">
        {NAV.map(item => (
          <button key={item.key} className={`bottom-nav-item${page === item.key ? " active" : ""}`}
            onClick={() => setPage(item.key)}>
            <span className="nav-icon">{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
        <button className="bottom-nav-item" onClick={logout}>
          <span className="nav-icon">⎋</span>
          <span>Out</span>
        </button>
      </nav>
    </div>
  );
}

// ── Detect portal route at MODULE LOAD TIME ──────────────────────────────────
// Runs when the JS bundle is parsed — before React, before AuthProvider,
// before Supabase reads localStorage. Cannot be overridden by auth state.
const PORTAL_TOKEN = (() => {
  const m = window.location.pathname.match(/^\/portal\/([^/]+)/);
  return m ? m[1] : null;
})();

export default function App() {
  // PORTAL_TOKEN is a module-level constant — if set, render portal only.
  // AuthProvider never mounts. No session read. No redirect possible.
  if (PORTAL_TOKEN) {
    return <SubscriberPortal token={PORTAL_TOKEN} />;
  }
  return <AuthProvider><AppInner /></AuthProvider>;
}
